import axios from "../config/connections"
import { sendCaptureExceptionToSentry } from "../utils/sentry"
import config from "../config"
import {
  GeometryWithCoordinates,
  IGraphFeatureOptions,
  IRecord,
} from "interfaces"

interface IFavIcon {
  size: string
  src: string
  type: string
}

interface IMenuConfig {
  to: string
  label: string
  order?: number
  icon_comp?: string
  icon_path?: string
  image?: string
  hidden?: boolean
}

export interface IMapExtrudeConfig {
  category: string
  height?: number
  offset?: number
  bottomHeight?: number
}

interface IMapLightConfig {
  color: string
  position?: number[]
  intensity: number
}

export interface IExtrudeConfig {
  kiosk: IMapExtrudeConfig[]
  unit: IMapExtrudeConfig[]
}

interface ILightConfig {
  ambientLight: IMapLightConfig
  directionalLight: IMapLightConfig
}

interface IShortestPathConfig {
  resolution: number
  // Request per-turn instructions from the venue SDK (venue-sdk strategy only). Defaults to false.
  include_instructions?: boolean
  // venue-sdk strategy only. Logs the walkable-path grid to the console. Defaults to false.
  debug?: boolean
}

type AmenityHighlightMode = "2DMarker"

export interface IAllVenuesViewConfig {
  zoom?: number
  pitch?: number
  center?: Coordinate
  bearing?: number
}

export interface IMapAnimation {
  [k: string]: {
    enable?: boolean
    speed?: number
  }
}

export interface IBasemapConfig {
  // Set to `false` to render no basemap tile layer at all.
  enabled?: boolean
  url_template?: string
  subdomains?: string[]
  opacity?: number
  // CSS filter applied to the tile layer only (e.g. "grayscale(1)"); leave
  // unset/empty for providers whose tiles are already styled.
  css_filter?: string
  max_available_zoom?: number
}

export interface IMapConfig {
  amenity_highlight_mode?: AmenityHighlightMode
  default_zoom?: number
  max_zoom?: number
  min_zoom?: number
  kiosk_pixel_ratio?: number
  mobile_pixel_ratio?: number
  extrude?: IExtrudeConfig
  light?: ILightConfig
  shortest_path?: IShortestPathConfig
  basemap?: IBasemapConfig
  text_marker_type?: "marker" | "ui-marker"
  // Add space around highlighted features with `extent_features_scale_factor` to prevent markers from going out of frame.
  extent_features_scale_factor?: number
  // Scale factor to expand the area limit defined by setMaxExtent on the map.
  extent_scale_map_view_factor?: number
  all_venues_view?: IAllVenuesViewConfig
  animations?: IMapAnimation[]
  prepare_steps?: IGraphFeatureOptions
  // Highlight rendering mode for polygon features. "auto" derives the fill by darkening the feature's default color by `highlight_polygon_color_ratio`; "manual" uses the symbol's polygonFill as-is.
  highlight_polygon_mode?: "auto" | "manual"
  // Darken ratio (0–1) applied to the default polygon fill when `highlight_polygon_mode` is "auto". Consumer defaults to 0.25 if unset.
  highlight_polygon_color_ratio?: number
}

export interface ISponsorContentConfig {
  disable?: boolean
  limit?: number
  frequency?: number
}

export type DurationUnitsObjectType = {
  hours?: number
  milliseconds?: number
  seconds?: number
  minutes?: number
  days?: number
  months?: number
  years?: number
  weeks?: number
}

interface INavigationConfig {
  main: IMenuConfig[]
  menus: IMenuConfig[]
  index?: string
  defaultRoutePath?: string
}

interface ISearchKeyConfig {
  name: string
  weight: number
}
export interface ISearchConfig {
  featureTypes: string[]
  keys: ISearchKeyConfig[]
}

export interface IStepConfig {
  delay: number
  enable: boolean
}

interface IAutoplayConfig {
  step: IStepConfig
}

export interface INewShopPageMetaData {
  periodInDays: number
}
export interface IPromotionPageMetaData {
  filter?: {
    category?: "shop" | "store" | "all"
  }
}

export interface IEventPageMetaData {
  displayMode?: "grouped" | "all"
}

export interface IDirectoryPageMetaData {
  multipleLocations?: boolean
  periodInDays?: number
}

export interface IIdleModeConfig {
  enable?: boolean
  timeout?: DurationUnitsObjectType["milliseconds"]
  throttle?: DurationUnitsObjectType["milliseconds"]
  redirectPath?: string
  disabled?: boolean
}

export interface IParkingFinderConfig {
  enable: boolean
  expire: DurationUnitsObjectType
  // Hides the Stores/Parking search-mode toggle when false; search is locked to stores.
  enableSearchMode?: boolean
}

interface Location {
  id: string
  name: {
    en: string
    [k: string]: string
  }
  image?: string
  location?: {
    id: string
  }
}

type IExtensionConfig = {
  options: {
    locations?: Location[]
    [k: string]: any
  }
}

export interface IVenueControllerConfig {
  // Enforce the use of a specific venue ID or `ALL` filter throughout the application.
  forceVenueId?: string
}
export interface IIFrameBridgeConfig {
  enabled?: boolean
  iframeOrigin?: string
  iframeUrl?: string
  // How long the parent waits for the child's bridge handshake before giving
  // up. Accepts the same Day.js duration-object format as IIdleModeConfig's
  // timeout/throttle, e.g. `{ seconds: 30 }`.
  readyTimeout?: DurationUnitsObjectType["milliseconds"]
  // Generic reason -> label for whatever debug UI the mounting feature shows
  // (e.g. a short on-screen code). Passed straight through to BridgeProvider
  // — this config type doesn't interpret or default these values itself, so
  // it stays reusable by any iframeBridge consumer, not just one feature.
  diagnosticLabels?: {
    syncNotSeen?: string
    ackNotReceived?: string
    originRejected?: string
  }
}

// Supported for ['en', 'th', 'zh', 'ja', 'ar']
export type Language = "en" | "th" | "zh" | "ja" | "ar"
export interface IKeyboardConfig {
  languages: Language[]
}

export type PageConfig<TMetaData = Record<string, any>> = {
  layout?: string
  metadata?: TMetaData
}

export type PageConfigs = {
  newShop?: PageConfig<INewShopPageMetaData>
  promotion?: PageConfig<IPromotionPageMetaData>
  event?: PageConfig<IEventPageMetaData>
  directory?: PageConfig<IDirectoryPageMetaData>
  [k: string]: PageConfig
}

export type ScreenSaverProvider = "splitr" | "local"

export interface IScreenSaverConfig {
  enable?: boolean
  provider?: ScreenSaverProvider
  refetchInterval?: number
}

export interface IDeviceConfig {
  navigation: INavigationConfig
  search: ISearchConfig
  autoplay?: IAutoplayConfig
  sponsor: {
    [k: string]: ISponsorContentConfig
  }
  featureQuickSearch: string[]
  venueFeatureQuickSearch: {
    [k: string]: string[]
  }
  idleMode?: IIdleModeConfig
  extensions?: {
    [k: string]: IExtensionConfig
  }
  iframeBridge?: IIFrameBridgeConfig
  keyboard?: IKeyboardConfig
  pages?: PageConfigs
  venueController?: IVenueControllerConfig
  screensaver?: IScreenSaverConfig
}

export interface IAppConfig {
  homeNavigations: IMenuConfig[]
  kiosk: IDeviceConfig
  "kiosk-h": IDeviceConfig
  "kiosk-v": IDeviceConfig
  kioskNavigations: IMenuConfig[]
  map: IMapConfig
  mobile: IDeviceConfig
}
export interface IKioskConfigRecord {
  id: number | string
  attributes: {
    name: string
    description?: string
    device_latitude: number
    device_longitude: number
    ordinal: number
    bearing?: number
    zoom?: number
    pitch?: number

    uptime_heartbeat_url?: string
    uptime_monitor_id?: string

    properties: { [k: string]: any }
  }
}
interface IAppManifest {
  icons: IFavIcon[]
  name: string
  theme_color?: string
}

export type Coordinate = [number, number]
type LinearRingCoordinates = Coordinate[]
type PolygonCoordinates = LinearRingCoordinates[]
type MultiPolygonCoordinates = PolygonCoordinates[]

interface IMapLabel {
  name: string
  geometry: {
    coordinates: PolygonCoordinates | MultiPolygonCoordinates
  }
  properties: Record<string, any>
}

interface IMapDecoration {
  name: string
  geometry: GeometryWithCoordinates
}

const get = async <T>(endpoint: string): Promise<T> => {
  try {
    const res = await axios.get(endpoint)
    if (res.status !== 200) {
      throw new Error()
    }
    return res.data as T
  } catch (err) {
    throw err.message
  }
}

export const fetchAppManifest = async () => {
  try {
    const path = `/manifest.json`
    const jsonRes = await get<IAppManifest>(path)
    return jsonRes
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch data from /manifest.json endpoint.`,
    })

    // Return empty object if error
    return {}
  }
}

export const fetchSponsoredContent = async () => {
  try {
    const path = `/sponsored-content.json`
    const jsonRes = await get<IRecord[]>(path)
    return jsonRes
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch data from /sponsored-content.json endpoint.`,
    })

    // Return empty object if error
    return []
  }
}

export const fetchMapLabels = async () => {
  const path = `/labels.geojson`
  try {
    const jsonRes = await get<IMapLabel[]>(path)
    return jsonRes
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch data from ${path} endpoint.`,
    })
    // Return empty array if error
    return { data: [] }
  }
}

export const fetchAppConfig = async () => {
  try {
    const path = `${config("host")}/delivery/config/${config("appConfigId")}`
    const jsonRes = await get<IAppConfig>(path)
    return jsonRes
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch App config endpoint.`,
    })

    // Return null if error
    return null
  }
}

export const fetchKioskConfig = async (name: string) => {
  try {
    if (!name) throw new Error()
    const path = `/kiosk-config.json?name=${name}`
    const jsonRes = await get<IKioskConfigRecord[]>(path)
    return jsonRes
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch Kiosk config endpoint.`,
    })

    // Return null if error
    return null
  }
}

export const fetchMapDecorations = async () => {
  const path = `/elements`
  try {
    const jsonResponse = await get<IMapDecoration[]>(path)
    return jsonResponse
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch data from ${path} endpoint.`,
    })
    return { data: [] }
  }
}

export const fetchPagesContent = async () => {
  const path = `${config("host")}/delivery/projects/${config(
    "projectId"
  )}/pages.geojson`
  try {
    const jsonRes = await get<IRecord[]>(path)
    return jsonRes
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch data from ${path} endpoint.`,
    })

    return []
  }
}
